// src/features/HomePage/HomePage.jsx
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Pie, Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  LineElement,
  ArcElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import SessionExpiredPopup from "../../components/SessionExpiredPopup/SessionExpiredPopup";
import useSessionExpired from "../../hooks/useSessionExpired";
import axios from "axios";
import { styled } from "@mui/material/styles";
import {
  Box,
  Card,
  Typography,
  Button,
  Paper,
  Stack,
  ButtonGroup,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import MuiSidebar from "../../components/MuiSidebar";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { alpha } from "@mui/material/styles";
import HomePageSkeleton from "../../components/HomePageSkeleton";

// Register ChartJS elements + Filler for 'fill: true'
ChartJS.register(
  LineElement,
  ArcElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Fallback demo data if backend returns no history
const initialChartData = {
  labels: [
    "2023-01-01",
    "2023-02-01",
    "2023-03-01",
    "2023-04-01",
    "2023-05-01",
    "2023-06-01",
  ],
  datasets: [
    {
      label: "Portfolio Value",
      data: [650000, 720000, 800000, 850000, 900000, 978500],
      borderColor: "#3B82F6",
      backgroundColor: "rgba(59, 130, 246, 0.1)",
      tension: 0.3,
      fill: true,
    },
  ],
};

const initialProfitChartData = {
  labels: [
    "2023-01-01",
    "2023-02-01",
    "2023-03-01",
    "2023-04-01",
    "2023-05-01",
    "2023-06-01",
  ],
  datasets: [
    {
      label: "Profit",
      data: [5000, 7000, 8000, 9000, 10000, 12000],
      borderColor: "#10B981",
      backgroundColor: "rgba(16, 185, 129, 0.1)",
      tension: 0.3,
      fill: true,
    },
  ],
};

// ------- Error Boundary -------
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Caught error: ", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "20px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
          }}
        >
          <h2>Something went wrong.</h2>
          <p>{this.state.error && this.state.error.toString()}</p>
          {this.state.errorInfo && (
            <details style={{ whiteSpace: "pre-wrap" }}>
              {this.state.errorInfo.componentStack}
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// ------- Styled Components -------
const StyledSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: theme.shadows[1],
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const ChartSection = styled(StyledSection)(({ theme }) => ({
  ".chart-controls-bar": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  ".charts-container": {
    display: "flex",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
}));

// Use plain div (CSS grid) instead of MUI Grid to avoid legacy warnings
const StatsGrid = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  width: "100%",
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));

const StatCard = styled(Card)(({ theme, accentcolor }) => ({
  height: "100%",
  width: "100%",
  position: "relative",
  padding: theme.spacing(2),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(10px)",
  transition: "all 0.3s ease-in-out",
  display: "flex",
  flexDirection: "column",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: accentcolor || theme.palette.primary.main,
  },
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[10],
    "& .MuiTypography-h4": {
      color: accentcolor,
    },
  },
  "& .MuiTypography-h4": {
    transition: "color 0.3s ease",
    fontSize: "1.75rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const ChartControls = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
  alignItems: "center",
  "& .MuiButton-root": {
    minWidth: 80,
    [theme.breakpoints.down("sm")]: {
      minWidth: 70,
      fontSize: "0.8rem",
      padding: theme.spacing(0.75, 1.5),
    },
  },
  "& .MuiSelect-root": {
    [theme.breakpoints.down("sm")]: {
      minWidth: "100px !important",
    },
  },
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

const RootLayout = styled("div")(({ theme }) => ({
  display: "flex",
  minHeight: "100vh",
  backgroundColor: "transparent",
  overflow: "hidden",
  width: "100%",
  maxWidth: "100vw",
  position: "relative",
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  minHeight: "100vh",
  overflow: "auto",
  padding: theme.spacing(3),
  position: "relative",
  zIndex: 1,
  backdropFilter: "blur(5px)",
  [theme.breakpoints.up("md")]: {
    width: "100%",
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(7),
    width: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(7),
  },
}));

const AnimatedBackground = styled("div")(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(225deg, 
    ${theme.palette.background.default} 0%,
    ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
  zIndex: -1,
  animation: "gradientAnimation 15s ease infinite",
  "@keyframes gradientAnimation": {
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  "& .MuiTableCell-head": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
  },
}));

const PieChartCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(10px)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
}));

// ------- Component -------
const HomePage = () => {
  const chartRef = useRef(null);
  const [profitChangeLabel] = useState("This Month");
  const [chartType, setChartType] = useState("capital"); // 'capital' or 'profit'
  const [periodFilter, setPeriodFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [apiDateRange, setApiDateRange] = useState({ start: null, end: null });
  const [graphData, setGraphData] = useState({ capital: {}, profit: {} });
  const [expandedSections, setExpandedSections] = useState({
    transactions: true,
  });

  // transaction controls
  const [transactionTypeFilter, setTransactionTypeFilter] = useState("all"); // 'all' | 'deposit' | 'withdraw'
  const [txnDateFilter, setTxnDateFilter] = useState({
    start: null,
    end: null,
  }); // Date objects
  const [transactionSort, setTransactionSort] = useState({
    by: "date",
    dir: "desc",
  }); // {by: 'date'|'amount', dir: 'asc'|'desc' }

  const [dashboardData, setDashboardData] = useState({
    cash: 0,
    assets: 0,
    equity: 0,
    last_month: 0,
    equity_percent: 0,
    total_profit: 0,
    current_month_profit: 0,
    net_deposit: 0,
    total_deposit: 0,
    total_withdraw: 0,
    roi: 0,
    transactions: [],
    user: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterMethod, setFilterMethod] = useState("period"); // 'period' or 'range'
  const { isSessionExpired, handleSessionExpired, closeSessionExpired } =
    useSessionExpired();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [transactionSearch, setTransactionSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [currency, setCurrency] = useState("USD");

  // Allocation (Pie) data uses dashboardData.cash / assets
  const allocationData = {
    labels: ["Cash", "Assets"],
    datasets: [
      {
        data: [dashboardData?.cash ?? 0, dashboardData?.assets ?? 0],
        backgroundColor: ["#3B82F6", "#10B981"],
        hoverBackgroundColor: ["#2563EB", "#065F46"],
      },
    ],
  };

  // Clean up chart on unmount
  useEffect(() => {
    const inst = chartRef.current;
    return () => {
      if (inst?.destroy) {
        try {
          inst.destroy();
        } catch { }
      }
    };
  }, []);

  // Token getter (keeps previous behavior)
  const getValidToken = useCallback(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      handleSessionExpired();
      return null;
    }
    return token;
  }, [handleSessionExpired]);

  // Helper number formatter
  const formatNumber = (value) => {
    if (value === null || value === undefined) return "N/A";
    const n = Number(value);
    if (Number.isNaN(n)) return "N/A";
    return n.toLocaleString();
  };

  // Fetch dashboard
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError("");

      try {
        const token = getValidToken();
        if (!token) return;

        axios.defaults.headers.common.Authorization = `Bearer ${token}`;

        const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const response = await axios.get(`${base}/home/dashboard`);

        const ok =
          response.data?.success === true ||
          response.data?.status === "success";
        if (!ok) throw new Error(response.data?.message || "Failed to fetch");

        const d = response.data.data || {};
        const apiStart = d.date_range?.start_date
          ? new Date(d.date_range.start_date)
          : null;
        const apiEnd = d.date_range?.end_date
          ? new Date(d.date_range.end_date)
          : null;
        // Map dashboard basics
        const mappedDashboard = {
          equity: Number(d.financial_summary?.equity?.amount ?? 0),
          last_month: Number(d.financial_summary?.equity?.change_percent ?? 0),
          equity_percent: Number(d.financial_summary?.equity?.percentage ?? 0),
          total_profit: Number(d.financial_summary?.profit?.total ?? 0),
          current_month_profit: Number(
            d.financial_summary?.profit?.this_month ?? 0
          ),
          net_deposit: Number(
            d.financial_summary?.deposits_withdrawals?.net_deposit ?? 0
          ),
          total_deposit: Number(
            d.financial_summary?.deposits_withdrawals?.total_deposits ?? 0
          ),
          total_withdraw: Number(
            d.financial_summary?.deposits_withdrawals?.total_withdrawals ?? 0
          ),
          roi: Number(d.financial_summary?.roi?.percentage ?? 0),
          // pie chart data here
          cash: Number(d.financial_summary?.cash ?? 0),
          assets: Number(d.financial_summary?.asset ?? 0),
          transactions: Array.isArray(d.recent_transactions?.data)
            ? d.recent_transactions.data.map((tx) => ({
              id: tx.id,
              type: tx.transaction_type,
              amount: Number(tx.amount),
              date: tx.transaction_date,
              status: tx.status,
              description:
                tx.description ||
                tx.note ||
                tx.type ||
                tx.transaction_type ||
                "",
            }))
            : [],
          user: d.user || null,
        };

        // Build historical series from d.historical_data
        // Build historical series from d.historical_data first
        const monthly_equity = {};
        const monthly_profit = {};
        if (Array.isArray(d.historical_data)) {
          d.historical_data.forEach((m) => {
            const key = `${m.month}-01`; // normalize to YYYY-MM-01
            monthly_equity[key] = Number(m.equity ?? 0);

            // Prefer total_profit if provided; otherwise sum asset+operation
            const totalProfitFromRow =
              m.total_profit !== undefined && m.total_profit !== null
                ? Number(m.total_profit)
                : Number(m.profit_asset ?? 0) + Number(m.profit_operation ?? 0);

            monthly_profit[key] = Number.isFinite(totalProfitFromRow)
              ? totalProfitFromRow
              : 0;
          });
        }

        // Derive useful profit numbers from historical_data when API summary is empty/zero
        const monthKeys = Object.keys(monthly_profit).sort(
          (a, b) => new Date(a) - new Date(b)
        );
        const latestMonthKey = monthKeys.length
          ? monthKeys[monthKeys.length - 1]
          : null;
        const latestMonthProfit = latestMonthKey
          ? Number(monthly_profit[latestMonthKey] ?? 0)
          : 0;

        // Sum of all monthly profits (useful fallback if financial_summary.profit.total is missing)
        const sumAllMonthlyProfit = monthKeys.reduce(
          (acc, k) => acc + Number(monthly_profit[k] ?? 0),
          0
        );

        // Commit state
        setDashboardData(mappedDashboard);
        setGraphData({ capital: monthly_equity, profit: monthly_profit });

        // Determine display dateRange (Date objects) using months keys
        const months = Object.keys(monthly_equity).sort(
          (a, b) => new Date(a) - new Date(b)
        );
        if (months.length > 0) {
          setDateRange({
            start: new Date(months[0]),
            end: new Date(months[months.length - 1]),
          });
        } else {
          setDateRange({ start: apiStart, end: apiEnd });
        }

        // set currency from API (fallback to USD)
        const apiCurrency =
          d.financial_summary?.equity?.currency ||
          d.financial_summary?.profit?.currency ||
          d.financial_summary?.deposits_withdrawals?.currency ||
          "USD";
        setCurrency(apiCurrency);
      } catch (error) {
        if (
          error.response?.data?.message?.includes("token") ||
          error.response?.status === 401 ||
          error.message?.includes("token")
        ) {
          handleSessionExpired();
          return;
        }
        console.error("Dashboard fetch error:", error);
        setError(error.message || "Failed to fetch dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [handleSessionExpired, getValidToken]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Filter helpers
  const filterDataByRange = (data, start, end) => {
    const months = Object.keys(data).sort((a, b) => new Date(a) - new Date(b));
    if (!months.length) return {};

    const toKey = (d) => {
      if (!d) return null;
      if (typeof d === "string") return d;
      if (d instanceof Date && !Number.isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        return `${y}-${m}-01`;
      }
      return null;
    };

    const startKey = toKey(start) || months[0];
    const endKey = toKey(end) || months[months.length - 1];

    const filtered = months.filter((m) => {
      const dt = new Date(m);
      return dt >= new Date(startKey) && dt <= new Date(endKey);
    });

    return Object.fromEntries(filtered.map((month) => [month, data[month]]));
  };

  const filterDataByPeriod = (data, period) => {
    const months = Object.keys(data).sort((a, b) => new Date(a) - new Date(b));
    switch (period) {
      case "3m":
        return Object.fromEntries(months.slice(-3).map((m) => [m, data[m]]));
      case "6m":
        return Object.fromEntries(months.slice(-6).map((m) => [m, data[m]]));
      case "1y":
        return Object.fromEntries(months.slice(-12).map((m) => [m, data[m]]));
      default:
        return data;
    }
  };

  const handlePeriodChange = (e) => {
    setPeriodFilter(e.target.value);
    setFilterMethod("period");
  };

  const handleDateRangeChange = (type, date) => {
    if (!date) return;
    const formattedDate = new Date(date);
    setDateRange((prev) => ({ ...prev, [type]: formattedDate }));
    setFilterMethod("range");
    setPeriodFilter("all");
  };

  // Sidebar responsiveness
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Pagination / Load more
  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  // When dashboard transactions change, ensure page resets
  useEffect(() => {
    setCurrentPage(1);
  }, [dashboardData.transactions]);

  // Reset page when filters/search/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    transactionSearch,
    transactionTypeFilter,
    txnDateFilter.start,
    txnDateFilter.end,
    transactionSort.by,
    transactionSort.dir,
  ]);

  // Compute filtered & sorted transactions (memoized)
  const computedTransactions = useMemo(() => {
    const list = Array.isArray(dashboardData?.transactions)
      ? dashboardData.transactions
      : [];

    // 1) search filter
    const q = (transactionSearch || "").trim().toLowerCase();
    const searched = q
      ? list.filter(
        (tx) =>
          String(tx.id).toLowerCase().includes(q) ||
          (tx.type && tx.type.toLowerCase().includes(q)) ||
          (tx.description && tx.description.toLowerCase().includes(q))
      )
      : list.slice();

    // 2) type filter
    const typeFiltered =
      transactionTypeFilter === "all"
        ? searched
        : searched.filter((tx) => {
          const t = (tx.type || "").toLowerCase();
          if (transactionTypeFilter === "deposit")
            return t.includes("deposit");
          if (transactionTypeFilter === "withdraw")
            return t.includes("withdraw") || t.includes("withdrawal");
          return true;
        });

    // 3) date filter (txn.date expected ISO or parseable)
    const dateFiltered = (() => {
      if (!txnDateFilter.start && !txnDateFilter.end) return typeFiltered;
      const start = txnDateFilter.start ? new Date(txnDateFilter.start) : null;
      const end = txnDateFilter.end ? new Date(txnDateFilter.end) : null;
      return typeFiltered.filter((tx) => {
        const raw = tx.date || tx.transaction_date;
        if (!raw) return false;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return false;
        if (start && d < new Date(new Date(start).setHours(0, 0, 0, 0)))
          return false;
        if (end && d > new Date(new Date(end).setHours(23, 59, 59, 999)))
          return false;
        return true;
      });
    })();

    // 4) sort
    const sorted = dateFiltered.sort((a, b) => {
      const dir = transactionSort.dir === "asc" ? 1 : -1;
      if (transactionSort.by === "amount") {
        return dir * ((Number(a.amount) || 0) - (Number(b.amount) || 0));
      }
      // default: date
      const da = new Date(a.date || a.transaction_date || 0).getTime() || 0;
      const db = new Date(b.date || b.transaction_date || 0).getTime() || 0;
      return dir * (da - db);
    });

    return sorted;
  }, [
    dashboardData?.transactions,
    transactionSearch,
    transactionTypeFilter,
    txnDateFilter.start,
    txnDateFilter.end,
    transactionSort.by,
    transactionSort.dir,
  ]);

  // Chart series prep
  const activeSeriesObject =
    filterMethod === "period"
      ? periodFilter === "all"
        ? graphData[chartType]
        : filterDataByPeriod(graphData[chartType], periodFilter)
      : filterDataByRange(graphData[chartType], dateRange.start, dateRange.end);

  // Build points from activeSeriesObject (keys are 'YYYY-MM-01')
  let chartDatasetPoints = Object.entries(activeSeriesObject || {})
    .sort((a, b) => new Date(a[0]) - new Date(b[0]))
    .map(([date, value]) => ({ x: formatChartLabel(date), y: Number(value) }));

  // If empty, use fallback demo data depending on chartType
  if (chartDatasetPoints.length === 0) {
    const fallback =
      chartType === "capital" ? initialChartData : initialProfitChartData;
    chartDatasetPoints = fallback.datasets[0].data.map((val, i) => ({
      x: formatChartLabel(fallback.labels[i]),
      y: val,
    }));
  }

  const chartLabels = chartDatasetPoints.map((p) => p.x);

  // Pagination slice
  const shownCount = Math.min(
    currentPage * rowsPerPage,
    computedTransactions.length
  );

  return (
    <RootLayout>
      <AnimatedBackground />
      <MuiSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        variant={isMobile ? "temporary" : "permanent"}
      />
      <MainContent>
        <ErrorBoundary>
          {loading ? (
            <HomePageSkeleton />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <Typography variant="h4" sx={{ mb: 4 }}>
                Financial Overview
              </Typography>

              <StatsGrid>
                {/* Capital Card */}
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                  <StatCard accentcolor="#10B981">
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TrendingUpIcon />
                        <Typography variant="h6">Equity</Typography>
                      </Box>
                      <Typography variant="h4">
                        {formatNumber(dashboardData?.equity)}
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography color="text.secondary">
                          Monthly Change
                        </Typography>
                        <Typography
                          color={
                            dashboardData?.last_month < 0 ? "error" : "success"
                          }
                        >
                          {dashboardData?.last_month !== null &&
                            dashboardData?.last_month !== undefined
                            ? `${Math.abs(
                              Number(dashboardData.last_month)
                            ).toLocaleString()}%`
                            : "N/A"}
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography color="text.secondary">Equity %</Typography>
                        <Typography color="text.primary">
                          {dashboardData?.equity_percent !== null &&
                            dashboardData?.equity_percent !== undefined
                            ? `${Number(
                              dashboardData.equity_percent
                            ).toLocaleString()}%`
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </StatCard>
                </Box>

                {/* Profit Card */}
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                  <StatCard accentcolor="#3B82F6">
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PaymentIcon />
                        <Typography variant="h6">Profit</Typography>
                      </Box>
                      <Typography variant="h4">
                        {formatNumber(dashboardData?.total_profit)}
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography color="text.secondary">
                          {profitChangeLabel}
                        </Typography>
                        <Typography
                          color={
                            dashboardData?.current_month_profit >= 0
                              ? "success"
                              : "error"
                          }
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {formatNumber(
                            Math.abs(dashboardData?.current_month_profit ?? 0)
                          )}
                          {dashboardData?.current_month_profit >= 0 ? (
                            <FaArrowUp />
                          ) : (
                            <FaArrowDown />
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </StatCard>
                </Box>

                {/* Net Deposits Card */}
                <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                  <StatCard accentcolor="#FBBF24">
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccountBalanceIcon />
                        <Typography variant="h6">Net Deposits</Typography>
                      </Box>
                      <Typography variant="h4">
                        {formatNumber(dashboardData?.net_deposit)}
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography color="text.secondary">
                          Total Deposited
                        </Typography>
                        <Typography>
                          {formatNumber(dashboardData?.total_deposit)}
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography color="text.secondary">
                          Total Withdrawn
                        </Typography>
                        <Typography>
                          {formatNumber(dashboardData?.total_withdraw)}
                        </Typography>
                      </Box>
                    </Stack>
                  </StatCard>
                </Box>
              </StatsGrid>

              {/* Chart Section */}
              <ChartSection>
                <Box
                  display="flex"
                  gap={3}
                  flexDirection={{ xs: "column", md: "row" }}
                >
                  {/* Line Chart */}
                  <Box flex={2}>
                    <Typography variant="h5" gutterBottom>
                      Portfolio Overview
                    </Typography>

                    <ChartControls>
                      <Box
                        display="flex"
                        gap={2}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <ButtonGroup>
                          <Button
                            variant={
                              chartType === "capital" ? "contained" : "outlined"
                            }
                            onClick={() => setChartType("capital")}
                          >
                            Capital
                          </Button>
                          <Button
                            variant={
                              chartType === "profit" ? "contained" : "outlined"
                            }
                            onClick={() => setChartType("profit")}
                          >
                            Profit
                          </Button>
                        </ButtonGroup>

                        <Select
                          size="small"
                          value={filterMethod}
                          onChange={(e) => setFilterMethod(e.target.value)}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="period">Period</MenuItem>
                          <MenuItem value="range">Date Range</MenuItem>
                        </Select>

                        {filterMethod === "period" ? (
                          <Select
                            size="small"
                            value={periodFilter}
                            onChange={handlePeriodChange}
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value="all">All Time</MenuItem>
                            <MenuItem value="1y">Last Year</MenuItem>
                            <MenuItem value="6m">Last 6 Months</MenuItem>
                            <MenuItem value="3m">Last 3 Months</MenuItem>
                          </Select>
                        ) : (
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Box display="flex" gap={2}>
                              <DatePicker
                                label="Start Date"
                                value={dateRange.start}
                                onChange={(date) =>
                                  handleDateRangeChange("start", date)
                                }
                                views={["year", "month"]}
                                openTo="month"
                                minDate={apiDateRange.start || undefined}
                                maxDate={apiDateRange.end || undefined}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    sx: { width: 150 },
                                  },
                                }}
                              />
                              <DatePicker
                                label="End Date"
                                value={dateRange.end}
                                onChange={(date) =>
                                  handleDateRangeChange("end", date)
                                }
                                views={["year", "month"]}
                                openTo="month"
                                minDate={
                                  dateRange.start ||
                                  apiDateRange.start ||
                                  undefined
                                }
                                maxDate={apiDateRange.end || undefined}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    sx: { width: 150 },
                                  },
                                }}
                              />
                            </Box>
                          </LocalizationProvider>
                        )}
                      </Box>
                    </ChartControls>

                    <Box height={400}>
                      <Line
                        data={{
                          labels: chartLabels,
                          datasets: [
                            {
                              label:
                                chartType === "capital"
                                  ? "Portfolio Value"
                                  : "Monthly Profit",
                              data: chartDatasetPoints,
                              borderColor:
                                chartType === "capital" ? "#3B82F6" : "#10B981",
                              backgroundColor:
                                chartType === "capital"
                                  ? "rgba(59, 130, 246, 0.1)"
                                  : "rgba(16, 185, 129, 0.1)",
                              tension: 0.3,
                              fill: true,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          parsing: false, // we provide {x,y}
                          plugins: {
                            legend: {
                              position: "top",
                              labels: {
                                boxWidth: 12,
                                boxHeight: 12,
                                padding: 10,
                                color: theme.palette.text.primary,
                                font: { size: 14, weight: "bold" },
                              },
                            },
                            tooltip: {
                              backgroundColor: theme.palette.background.paper,
                              titleColor: theme.palette.text.primary,
                              bodyColor: theme.palette.text.secondary,
                              borderColor: theme.palette.divider,
                              borderWidth: 1,
                            },
                          },
                          scales: {
                            x: {
                              type: "category",
                              title: {
                                display: true,
                                text: "Date",
                                color: theme.palette.text.primary,
                                font: { size: 16, weight: "bold" },
                              },
                              ticks: {
                                color: theme.palette.text.secondary,
                                font: { size: 12 },
                              },
                              grid: {
                                color: theme.palette.divider,
                                lineWidth: 1,
                              },
                            },
                            y: {
                              title: {
                                display: true,
                                text:
                                  chartType === "capital"
                                    ? `Portfolio Value (${currency})`
                                    : `Monthly Profit (${currency})`,
                                color: theme.palette.text.primary,
                                font: { size: 16, weight: "bold" },
                              },
                              ticks: {
                                color: theme.palette.text.secondary,
                                font: { size: 12 },
                                callback: (value) =>
                                  `${Number(value).toLocaleString()}`,
                              },
                              grid: {
                                color: theme.palette.divider,
                                lineWidth: 1,
                              },
                            },
                          },
                        }}
                        ref={chartRef}
                      />
                    </Box>
                  </Box>

                  {/* Pie Chart */}
                  <PieChartCard elevation={1}>
                    <Typography variant="h6" gutterBottom>
                      Portfolio Distribution
                    </Typography>
                    <Box height={300} width="100%">
                      <Pie
                        data={allocationData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            datalabels: {
                              formatter: (value, context) => {
                                const data =
                                  context.chart.data.datasets[0].data || [];
                                const total = data.reduce(
                                  (a, b) => a + (Number(b) || 0),
                                  0
                                );
                                const pct = total ? (value / total) * 100 : 0;
                                const label =
                                  context.chart.data.labels?.[
                                  context.dataIndex
                                  ] ?? "";
                                // show: Label on first line, value + currency on second, % on third
                                return `${label}\n${Number(
                                  value
                                ).toLocaleString()} ${currency}\n${pct.toFixed(
                                  1
                                )}%`;
                              },
                              align: "center",
                              anchor: "center",
                              clamp: true,
                              font: { weight: "600" },
                              color: (ctx) =>
                                ctx.chart?.options?.color || "#111", // readable text
                            },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => {
                                  const value = Number(ctx.parsed) || 0;
                                  const dataset = ctx.dataset?.data || [];
                                  const total = dataset.reduce(
                                    (a, b) => a + (Number(b) || 0),
                                    0
                                  );
                                  const pct = total ? (value / total) * 100 : 0;
                                  return `${ctx.label
                                    }: ${value.toLocaleString()} ${currency} (${pct.toFixed(
                                      1
                                    )}%)`;
                                },
                              },
                            },
                          },
                        }}
                      />
                    </Box>

                    {/* Distribution Values */}
                    <Box width="100%" mt={2}>
                      <Stack spacing={1.5}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              width={12}
                              height={12}
                              bgcolor="#3B82F6"
                              borderRadius="50%"
                            />
                            <Typography>Cash</Typography>
                          </Box>
                          <Typography fontWeight="medium">
                            {formatNumber(dashboardData?.cash ?? 0)}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              width={12}
                              height={12}
                              bgcolor="#10B981"
                              borderRadius="50%"
                            />
                            <Typography>Assets</Typography>
                          </Box>
                          <Typography fontWeight="medium">
                            {formatNumber(dashboardData?.assets ?? 0)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </PieChartCard>
                </Box>
              </ChartSection>

              {/* Transactions */}
              <StyledSection>
                <Typography variant="h5" gutterBottom>
                  Transactions
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => toggleSection("transactions")}
                  sx={{ mb: 2 }}
                >
                  {expandedSections.transactions ? "Hide" : "Show"} Transactions
                </Button>

                {expandedSections.transactions && (
                  <Box>
                    {/* Controls row: search + type + date range + sort + reset */}
                    <Box
                      display="flex"
                      gap={{ xs: 1, sm: 2 }}
                      sx={{
                        mb: 3,
                        flexWrap: "wrap",
                        alignItems: "center",
                        "& > *": {
                          flex: { xs: "1 1 100%", sm: "0 1 auto" },
                        },
                      }}
                    >
                      <TextField
                        label="Search by ID, Description..."
                        variant="outlined"
                        size="small"
                        value={transactionSearch}
                        onChange={(e) => setTransactionSearch(e.target.value)}
                        sx={{
                          minWidth: { xs: "100%", sm: 220 },
                          flex: { xs: "1 1 100%", sm: "1 1 250px" }
                        }}
                      />

                      <Select
                        size="small"
                        value={transactionTypeFilter}
                        onChange={(e) =>
                          setTransactionTypeFilter(e.target.value)
                        }
                        sx={{
                          minWidth: { xs: "100%", sm: 140 },
                          flex: { xs: "1 1 100%", sm: "0 1 auto" },
                        }}
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="deposit">Deposits Only</MenuItem>
                        <MenuItem value="withdraw">Withdrawals Only</MenuItem>
                      </Select>

                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Box
                          display="flex"
                          gap={1}
                          sx={{
                            flex: { xs: "1 1 100%", sm: "0 1 auto" },
                            "& > *": { flex: 1 },
                          }}
                        >
                          <DatePicker
                            label="Start"
                            views={["year", "month", "day"]}
                            value={txnDateFilter.start}
                            onChange={(d) =>
                              setTxnDateFilter((prev) => ({ ...prev, start: d }))
                            }
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: { width: { xs: "100%", sm: 130 } }
                              },
                            }}
                            maxDate={txnDateFilter.end || undefined}
                          />
                          <DatePicker
                            label="End"
                            views={["year", "month", "day"]}
                            value={txnDateFilter.end}
                            onChange={(d) =>
                              setTxnDateFilter((prev) => ({ ...prev, end: d }))
                            }
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: { width: { xs: "100%", sm: 130 } }
                              },
                            }}
                            minDate={txnDateFilter.start || undefined}
                          />
                        </Box>
                      </LocalizationProvider>

                      <Select
                        size="small"
                        value={`${transactionSort.by}_${transactionSort.dir}`}
                        onChange={(e) => {
                          const [by, dir] = e.target.value.split("_");
                          setTransactionSort({ by, dir });
                        }}
                        sx={{
                          minWidth: { xs: "100%", sm: 160 },
                          flex: { xs: "1 1 100%", sm: "0 1 auto" },
                        }}
                      >
                        <MenuItem value="date_desc">Date: Newest</MenuItem>
                        <MenuItem value="date_asc">Date: Oldest</MenuItem>
                        <MenuItem value="amount_desc">
                          Amount: High → Low
                        </MenuItem>
                        <MenuItem value="amount_asc">
                          Amount: Low → High
                        </MenuItem>
                      </Select>

                      <Button
                        variant="outlined"
                        onClick={() => {
                          setTransactionTypeFilter("all");
                          setTxnDateFilter({ start: null, end: null });
                          setTransactionSort({ by: "date", dir: "desc" });
                          setTransactionSearch("");
                          setCurrentPage(1);
                        }}
                        sx={{
                          flex: { xs: "1 1 100%", sm: "0 1 auto" },
                        }}
                      >
                        Reset Filters
                      </Button>
                    </Box>

                    <TableContainer
                      component={Paper}
                      sx={{
                        overflowX: "auto",
                        "& .MuiTable-root": {
                          minWidth: { xs: 500, sm: "auto" },
                        },
                      }}
                    >
                      <Table>
                        <StyledTableHead>
                          <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Date</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </StyledTableHead>
                        <TableBody>
                          {computedTransactions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                {loading ? (
                                  <CircularProgress size={24} />
                                ) : (
                                  "No transactions found"
                                )}
                              </TableCell>
                            </TableRow>
                          ) : (
                            computedTransactions
                              .slice(0, shownCount)
                              .map((tx) => (
                                <TableRow key={tx.id}>
                                  <TableCell>
                                    <Typography
                                      sx={{
                                        color: (tx.type || "")
                                          .toLowerCase()
                                          .includes("withdraw")
                                          ? "error.main"
                                          : "success.main",
                                        fontWeight: "medium",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      {(tx.type || "")
                                        .toLowerCase()
                                        .includes("withdraw") ? (
                                        <FaArrowDown />
                                      ) : (
                                        <FaArrowUp />
                                      )}
                                      {tx.description ||
                                        tx.type ||
                                        tx.transaction_type ||
                                        "—"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        gap: 0.5,
                                        color: (tx.type || "")
                                          .toLowerCase()
                                          .includes("withdraw")
                                          ? "error.main"
                                          : "success.main",
                                        fontWeight: "medium",
                                      }}
                                    >
                                      {Math.abs(
                                        Number(tx.amount) || 0
                                      ).toLocaleString()}{" "}
                                      {currency}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {tx.date
                                      ? new Date(tx.date).toLocaleDateString()
                                      : "-"}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={tx.status}
                                      color={
                                        tx.status === "done" ||
                                          tx.status === "completed"
                                          ? "success"
                                          : "default"
                                      }
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mt: 2 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Showing {shownCount} of {computedTransactions.length}{" "}
                        transactions
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={
                          loading || shownCount >= computedTransactions.length
                        }
                      >
                        {loading ? <CircularProgress size={24} /> : "Load More"}
                      </Button>
                    </Box>
                  </Box>
                )}
              </StyledSection>
            </>
          )}
        </ErrorBoundary>
      </MainContent>

      <SessionExpiredPopup
        open={isSessionExpired}
        onClose={closeSessionExpired}
      />
    </RootLayout >
  );
};

export default HomePage;

// Helper: Format chart labels as "MMM YYYY" (e.g., "Jan 2025")
function formatChartLabel(date) {
  try {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return date;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short" });
  } catch {
    return date;
  }
}
